// How It Works — 9-step cinematic stepper showing the full Operator workflow.
// White scene card auto-cycles every 4.5s; hover to pause; click any step node to jump.
// Source canonical: design-system/project/homepage/index.html § HowItWorks.

import { useEffect, useState } from 'react';
import Portrait from '../components/Portrait';
import { ROSTER } from '../data/roster';

const STEP_LABELS = [
  'You type what you need',
  'Roster opens',
  'Profile review',
  'Hired',
  'MCP connections live',
  'Job posted',
  'Running · 3 gates green',
  'Output ships',
  'Memory updates',
] as const;

const TOTAL = STEP_LABELS.length;

const Mono = ({ children, color = '#1A1918', weight = 600, style = {} as React.CSSProperties }: { children: React.ReactNode; color?: string; weight?: number; style?: React.CSSProperties }) => (
  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: weight, color, ...style }}>{children}</span>
);

function Scene({ step }: { step: number }) {
  switch (step) {
    // ── 01 · Intent ─────────────────────────────────────────
    case 0: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #F0EBE3' }}>
          <div><Mono color="#D4835E" weight={700}>SAGE & SALT</Mono> <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#78716C' }}>· workspace</span></div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#2F6B4D' }}><span style={{ width: 6, height: 6, borderRadius: 9999, background: '#22C55E' }}/>live</span>
        </div>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 500, color: '#1A1918', marginBottom: 16 }}>What do you need help with today?</div>
        <div style={{ background: '#FAF6EF', border: '1px solid #E8DFD0', borderRadius: 12, padding: '16px 18px', marginBottom: 18 }}>
          <Mono color="#A25A38" weight={700}>YOU</Mono>
          <div style={{ fontSize: 15, color: '#1A1918', marginTop: 6, lineHeight: 1.55 }}>Write Shopify listings for the 240 new SKUs in <Mono>drive/skus.csv</Mono>. Brand voice: Sage & Salt.</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#78716C' }}>13 words · ↩ to send</div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#1A1918', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif' }}>Find me a role →</span>
        </div>
      </div>
    );

    // ── 02 · Roster opens ───────────────────────────────────
    case 1: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>4 AI Operators match your intent</div>
          <Mono color="#78716C">RANKED BY FIT</Mono>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #FAF6EF 0%, #FDE0D3 100%)', border: '1.5px solid #D4835E', borderRadius: 12, padding: 18, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Mono color="#A25A38" weight={700}>★ BEST MATCH · 96% FIT</Mono>
            <Mono color="#2F6B4D" weight={700}>● PRODUCTION</Mono>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <Portrait src={ROSTER[0].photo} name={ROSTER[0].name} size={52}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 17, fontWeight: 600, color: '#1A1918' }}>Priya Desai · Listings Specialist</div>
              <div style={{ fontSize: 12.5, color: '#57534E', marginTop: 4 }}>"Writes Shopify listings that actually sell."</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#78716C' }}>
            <span>model · <Mono>claude-sonnet-4</Mono></span>
            <span>·</span>
            <span><Mono>$0.05</Mono>/run</span>
            <span>·</span>
            <span>q · <Mono color="#2F6B4D">0.86</Mono></span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[{ n: 'Maya', r: 'Content Writer', f: '82%' }, { n: 'Aanya', r: 'SEO Expert', f: '74%' }, { n: 'Karan', r: 'Voice Tuner', f: '68%' }].map(c => (
            <div key={c.n} style={{ background: '#FAFAF8', border: '1px solid #E8E6E1', borderRadius: 10, padding: 12 }}>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 14, fontWeight: 600, color: '#1A1918' }}>{c.n}</div>
              <div style={{ fontSize: 11, color: '#78716C', marginTop: 2 }}>{c.r}</div>
              <Mono color="#A25A38" weight={700} style={{ marginTop: 6, display: 'inline-block', fontSize: 10 }}>{c.f} fit</Mono>
            </div>
          ))}
        </div>
      </div>
    );

    // ── 03 · Profile review ─────────────────────────────────
    case 2: return (
      <div>
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', marginBottom: 18 }}>
          <Portrait src={ROSTER[0].photo} name="Priya Desai" size={72}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 24, fontWeight: 600, color: '#1A1918', lineHeight: 1.1 }}>Priya Desai</div>
            <div style={{ fontSize: 13, color: '#57534E', marginTop: 4 }}>Listings Specialist · AI Role</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#78716C' }}>
              <span>model · <Mono>claude-sonnet-4</Mono></span>
              <span>·</span>
              <span>build · <Mono>v3.4.1</Mono></span>
              <span>·</span>
              <span>uptime · <Mono color="#2F6B4D">99.7%</Mono></span>
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: '14px 0', borderTop: '1px solid #F0EBE3', borderBottom: '1px solid #F0EBE3' }}>
          {([['per run', '$0.05'], ['speed', '90s'], ['quality', '0.86'], ['runs', '4.8k']] as const).map(([l, v]) => (
            <div key={l}>
              <Mono color="#78716C">{l}</Mono>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 18, fontWeight: 700, color: '#1A1918', marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <Mono color="#78716C">STACK FLUENCY</Mono>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {['Shopify', 'BigCommerce', 'WooCommerce'].map(s => <span key={s} style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#A25A38', background: '#FDE0D3', padding: '4px 9px', borderRadius: 4 }}>{s}</span>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ flex: 1, padding: '12px 18px', background: '#D4835E', color: '#1A1918', borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: 'Inter Tight, system-ui, sans-serif', textAlign: 'center' }}>Hire Priya · ₹15K/mo + tokens →</span>
          <span style={{ padding: '12px 18px', background: '#fff', color: '#1A1918', border: '1px solid #D6D3D1', borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif' }}>Sample output</span>
        </div>
      </div>
    );

    // ── 04 · Hired ──────────────────────────────────────────
    case 3: return (
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{ width: 64, height: 64, borderRadius: 9999, background: 'rgba(212,131,94,0.14)', border: '2px solid #D4835E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="m5 12 5 5L20 7" stroke="#D4835E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 500, color: '#1A1918', marginBottom: 6 }}>Priya Desai is hired.</div>
        <div style={{ fontSize: 14, color: '#57534E', marginBottom: 22 }}>Welcome message sent. She's setting up her workspace.</div>
        <div style={{ display: 'inline-grid', gridTemplateColumns: 'auto auto', gap: '10px 28px', textAlign: 'left', marginBottom: 22, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12 }}>
          <Mono color="#78716C">CONTRACT</Mono><Mono>Pay-per-output</Mono>
          <Mono color="#78716C">BUNDLE</Mono><Mono>50,000 outputs / mo</Mono>
          <Mono color="#78716C">PRICE</Mono><Mono>₹15K + token bundle</Mono>
          <Mono color="#78716C">STARTED</Mono><Mono color="#2F6B4D">just now (15:42 IST)</Mono>
        </div>
        <div style={{ padding: '12px 22px', background: '#1A1918', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: 'Inter Tight, system-ui, sans-serif', display: 'inline-block' }}>Set up tools →</div>
      </div>
    );

    // ── 05 · MCP connections ────────────────────────────────
    case 4: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>Connect Priya to your SaaS stack</div>
          <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#A25A38', background: '#FDE0D3', padding: '4px 9px', borderRadius: 4, letterSpacing: '0.06em' }}>MCP · BROKERED</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { n: 'Shopify',      s: 'connected' },
            { n: 'Google Drive', s: 'connected' },
            { n: 'Klaviyo',      s: 'connected' },
            { n: 'BigCommerce',  s: 'idle' },
            { n: 'WooCommerce',  s: 'idle' },
            { n: 'Webflow',      s: 'idle' },
          ].map(t => (
            <div key={t.n} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#FAFAF8', border: '1px solid #E8E6E1', borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: t.s === 'connected' ? '#22C55E' : '#D6D3D1', boxShadow: t.s === 'connected' ? '0 0 0 3px rgba(34,197,94,0.18)' : 'none' }}/>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1918' }}>{t.n}</span>
              </div>
              {t.s === 'connected' ? (
                <Mono color="#2F6B4D" weight={700}>✓ CONNECTED</Mono>
              ) : (
                <span style={{ padding: '5px 11px', border: '1px solid #D6D3D1', borderRadius: 6, fontFamily: 'Inter Tight, system-ui, sans-serif', fontSize: 12, fontWeight: 600, color: '#1A1918' }}>Connect</span>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#78716C' }}>3 tools authenticated · MCP brokered · Priya can now read + write</div>
      </div>
    );

    // ── 06 · Job posted ─────────────────────────────────────
    case 5: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #F0EBE3' }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>New job posted</div>
          <Mono color="#78716C">JOB #4892 · PRIYA</Mono>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px 24px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12 }}>
          <Mono color="#78716C">SOURCE</Mono><Mono>drive/skus.csv (240 SKUs)</Mono>
          <Mono color="#78716C">TARGET</Mono><Mono>shopify.products.create</Mono>
          <Mono color="#78716C">BRAND VOICE</Mono><Mono color="#A25A38">Sage & Salt (locked)</Mono>
          <Mono color="#78716C">SCHEMA</Mono><Mono>11 output fields</Mono>
          <Mono color="#78716C">QUALITY FLOOR</Mono><Mono>q ≥ 0.85</Mono>
          <Mono color="#78716C">COST CEILING</Mono><Mono>$0.05 / run</Mono>
        </div>
        <div style={{ marginTop: 22, padding: '14px 18px', background: '#FAF6EF', borderLeft: '3px solid #D4835E', borderRadius: 4, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#1A1918', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>● <Mono color="#A25A38">QUEUED</Mono> · accepted by priya.listing-v3</span>
          <Mono color="#78716C">ETA · 12 min</Mono>
        </div>
      </div>
    );

    // ── 07 · Running with 3 gates ───────────────────────────
    case 6: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #F0EBE3' }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>Job #4892 · Priya · running</div>
          <Mono color="#78716C">12m 34s ELAPSED</Mono>
        </div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <Mono color="#78716C">PROGRESS</Mono>
            <Mono>188 / 240 · 78%</Mono>
          </div>
          <div style={{ height: 8, background: '#F0EBE3', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: '78%', height: '100%', background: 'linear-gradient(90deg, #D4835E 0%, #E8A384 100%)', borderRadius: 4 }}/>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { g: 'GATE A', t: 'Net-positive', s: '0.91' },
            { g: 'GATE B', t: 'Field merge', s: '0.71' },
            { g: 'GATE C', t: 'Cost ceiling', s: '$0.043' },
          ].map(g => (
            <div key={g.g} style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.30)', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Mono color="#78716C" weight={700}>{g.g}</Mono>
                <Mono color="#2F6B4D" weight={700}>✓ PASS</Mono>
              </div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 15, fontWeight: 500, color: '#1A1918' }}>{g.t}</div>
              <Mono color="#1A1918" weight={700} style={{ display: 'inline-block', marginTop: 4 }}>{g.s}</Mono>
            </div>
          ))}
        </div>
        <Mono color="#A25A38">◐ AUTO-FLAGGED · 3 listings for review</Mono>
      </div>
    );

    // ── 08 · Output ships ───────────────────────────────────
    case 7: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid #F0EBE3' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 22, height: 22, borderRadius: 9999, background: '#22C55E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="m5 12 5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>Job complete · 12m 34s</div>
          </div>
          <Mono color="#A25A38">4 FLAGGED FOR REVIEW</Mono>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
          {([['Published', '240', '#1A1918'], ['Quality', '0.87', '#2F6B4D'], ['Cost', '$12.04', '#1A1918'], ['Rework', '0', '#1A1918']] as const).map(([l, v, c]) => (
            <div key={l} style={{ background: '#FAFAF8', borderRadius: 8, padding: 12 }}>
              <Mono color="#78716C">{l}</Mono>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 22, fontWeight: 700, color: c, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11.5, color: '#1A1918', lineHeight: 1.7, padding: '12px 14px', background: '#FAF6EF', borderRadius: 8 }}>
          <Mono color="#78716C">SAMPLE OUTPUTS</Mono>
          <div style={{ marginTop: 6 }}><span style={{ color: '#22C55E' }}>✓</span> Sandalwood Cleanser · 240ch · q=0.96 · published</div>
          <div><span style={{ color: '#22C55E' }}>✓</span> Glycolic Toner 100ml · 234ch · q=0.91 · published</div>
          <div><span style={{ color: '#D4835E' }}>◐</span> Vit-C Serum · 218ch · q=0.79 · flagged for review</div>
        </div>
      </div>
    );

    // ── 09 · Memory updates ─────────────────────────────────
    case 8: return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid #F0EBE3' }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, fontWeight: 500, color: '#1A1918' }}>Priya's memory updated</div>
          <Mono color="#A25A38">v3.4.1 → v3.4.2</Mono>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {[
            { tag: 'BRAND VOICE', body: '12 new patterns from approved outputs', count: '+12', color: '#2F6B4D' },
            { tag: 'VOCABULARY',  body: '34 brand-specific words locked',       count: '+34', color: '#2F6B4D' },
            { tag: 'EDGE CASES',  body: '4 new fallback rules added',           count: '+4',  color: '#2F6B4D' },
            { tag: 'NEGATIVE ✗',  body: '3 patterns to avoid going forward',    count: '+3',  color: '#A25A38' },
          ].map(m => (
            <div key={m.tag} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#FAFAF8', border: '1px solid #F0EBE3', borderRadius: 8 }}>
              <div>
                <Mono color={m.color} weight={700}>+ {m.tag}</Mono>
                <div style={{ fontSize: 13, color: '#1A1918', marginTop: 2 }}>{m.body}</div>
              </div>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 18, fontWeight: 700, color: m.color }}>{m.count}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg, rgba(212,131,94,0.10) 0%, rgba(34,197,94,0.10) 100%)', borderRadius: 8, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#1A1918', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Next batch projected: <Mono color="#2F6B4D" weight={700}>~14% higher q</Mono> · <Mono color="#2F6B4D" weight={700}>9% lower cost</Mono></span>
          <Mono color="#A25A38">READY FOR JOB #4893</Mono>
        </div>
      </div>
    );

    default: return null;
  }
}

export default function HowItWorks() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setStep(s => (s + 1) % TOTAL), 4500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section
      id="how"
      style={{
        padding: '96px 40px',
        background: 'linear-gradient(180deg, #0E0E0C 0%, #161514 50%, #0E0E0C 100%)',
        color: '#F5F4F2',
        position: 'relative', overflow: 'hidden',
        borderTop: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {/* Top hairline copper accent */}
      <div style={{
        position: 'absolute', top: 0, left: '20%', right: '20%', height: 1,
        background: 'linear-gradient(90deg, transparent 0%, rgba(212,131,94,0.40) 50%, transparent 100%)',
      }}/>
      {/* Right-side steel glow */}
      <div style={{
        position: 'absolute', top: '20%', right: -250, width: 600, height: 600, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(94,131,179,0.08) 0%, transparent 70%)',
      }}/>

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        <span style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: '#D4835E',
        }}>How it works · end to end</span>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 48, fontWeight: 400,
          fontVariationSettings: "'opsz' 96",
          letterSpacing: '-0.02em', lineHeight: 1.1,
          color: '#F5F4F2',
          margin: '10px 0 14px', maxWidth: 820,
        }}>
          From intent to outcome — in nine steps.
        </h2>
        <p style={{
          fontFamily: 'Inter Tight, system-ui, sans-serif',
          fontSize: 16, lineHeight: 1.6,
          color: '#C7C3BD',
          maxWidth: 680, margin: '0 0 40px',
        }}>
          Same workflow as hiring a human; 10× the throughput. Hover to pause; click any step to jump.
        </p>

        {/* Stepper */}
        <div className="how-stepper" style={{ display: 'flex', alignItems: 'center', marginBottom: 26 }}>
          {STEP_LABELS.map((lab, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <button
                onClick={() => setStep(i)}
                title={lab}
                style={{
                  position: 'relative', flexShrink: 0,
                  width: i === step ? 36 : 28,
                  height: i === step ? 36 : 28,
                  borderRadius: '50%',
                  background: i < step ? '#4ADE80' : i === step ? '#D4835E' : '#232120',
                  border: i === step ? '2px solid #D4835E' : '1px solid rgba(255,255,255,0.10)',
                  color: i === step ? '#1A1918' : i < step ? '#1A1918' : '#908A82',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: i === step ? 12 : 11, fontWeight: 700,
                  cursor: 'pointer', padding: 0,
                  boxShadow: i === step ? '0 0 0 5px rgba(212,131,94,0.18)' : 'none',
                  transition: 'all 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {i < step ? '✓' : String(i + 1).padStart(2, '0')}
              </button>
              {i < TOTAL - 1 && (
                <div style={{ flex: 1, height: 2, background: i < step ? '#4ADE80' : 'rgba(255,255,255,0.08)', margin: '0 6px', transition: 'background 300ms ease' }}/>
              )}
            </div>
          ))}
        </div>

        {/* Active step label */}
        <div style={{ marginBottom: 14 }}>
          <Mono color="#D4835E" weight={700}>STEP {String(step + 1).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}</Mono>
          <h3 style={{
            fontFamily: 'Fraunces, serif',
            fontSize: 28, fontWeight: 500,
            color: '#F5F4F2',
            margin: '4px 0 0', lineHeight: 1.2,
          }}>
            {STEP_LABELS[step]}
          </h3>
        </div>

        {/* White scene card — auto-cycles, hover to pause */}
        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          style={{
            background: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16,
            padding: 32,
            minHeight: 380,
            boxShadow: '0 32px 80px -24px rgba(0,0,0,0.65), 0 0 40px -10px rgba(212,131,94,0.18)',
            color: '#1A1918',
            position: 'relative', overflow: 'hidden',
          }}
        >
          <Scene step={step} />
        </div>
      </div>
    </section>
  );
}
